import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Cpu, Radio, Thermometer, Bot } from 'lucide-react';
import PageBanner from '../components/common/PageBanner';
import { Section, Head, Eyebrow, Card, Index, Pill, Spec, FlowArrow } from '../components/common/Primitives';
import {
  fieldLayer, transportLayer, edgeLayer, explanationLayer,
} from '../data/system';
import banner from '../assets/banners/architecture.jpg';
import soilProbes from '../assets/tech/soil-probes.jpg';
import assistantApp from '../assets/tech/assistant-app.jpg';

/* Matches `xl:gap-x-10` on the hop row below: the arrow is drawn to span
   exactly one gap, so the two numbers have to move together. */
const HOP_GAP = 40;

const jump = [
  { id: 'field', label: '01 · Field sensing' },
  { id: 'transport', label: '02 · Transport & RF' },
  { id: 'edge', label: '03 · Edge compute' },
  { id: 'explanation', label: '06 · Explanation' },
];

const Bullet = ({ children, tone = 'light' }) => (
  <li className="flex items-start gap-3">
    <Check className={`w-4 h-4 mt-1 shrink-0 ${tone === 'dark' ? 'text-lime' : 'text-forest'}`} />
    <span className={`text-[14.5px] leading-relaxed ${tone === 'dark' ? 'text-paper/70' : 'text-soil/75'}`}>
      {children}
    </span>
  </li>
);

const Architecture = () => (
  <div>
    <PageBanner
      image={banner}
      kicker="Farm Tech · Architecture"
      title="Layer by layer"
      lead="What each layer of the nutrient system is responsible for, what that responsibility demanded of it, and what it hands to the layer above."
    >
      <div className="flex flex-wrap gap-2">
        {jump.map((j) => (
          <a
            key={j.id}
            href={`#${j.id}`}
            className="label rounded-full px-3.5 py-2 bg-white/[0.06] ring-1 ring-white/15 text-paper/70 hover:text-lime hover:ring-lime/40 transition-colors"
          >
            {j.label}
          </a>
        ))}
      </div>
    </PageBanner>

    {/* ---------- Layer 1 · Field ---------- */}
    <Section tone="paper" id="field">
      <Head eyebrow="Layer 01 · Field" title="Field sensing" lead={fieldLayer.intro} />

      <div className="mt-12 grid lg:grid-cols-2 gap-6">
        <Card className="p-7">
          <p className="label text-soil/40 flex items-center gap-2">
            <Cpu className="w-3.5 h-3.5" /> Controller / radio
          </p>
          <h3 className="mt-3 font-display text-[1.35rem] font-semibold text-forest">
            {fieldLayer.controller.title}
          </h3>
          <ul className="mt-5 space-y-3">
            {fieldLayer.controller.points.map((p) => (
              <Bullet key={p}>{p}</Bullet>
            ))}
          </ul>
        </Card>

        <div className="rounded-xl bg-soil text-paper p-7 ring-1 ring-soil">
          <p className="label text-lime flex items-center gap-2">
            <Thermometer className="w-3.5 h-3.5" /> Soil probe
          </p>
          <h3 className="mt-3 font-display text-[1.35rem] font-semibold text-paper">
            {fieldLayer.probe.title}
          </h3>
          <div className="mt-5 flex flex-wrap gap-2">
            {fieldLayer.probe.measures.map((m) => (
              <Pill key={m} tone="dark">{m}</Pill>
            ))}
          </div>
          <p className="mt-5 text-paper/60 text-[14.5px] leading-relaxed">{fieldLayer.probe.body}</p>
        </div>
      </div>

      {/* Sensing cycle - a real sequence, so it is numbered. The probe photo
          belongs with the cycle it performs, and fills its column exactly. */}
      <div className="mt-8 grid lg:grid-cols-[1fr_18rem] gap-8 items-stretch rounded-xl bg-chalk ring-1 ring-soil/10 p-7">
        <div>
          <p className="label text-forest">Sensing cycle</p>
          {/* The separator belongs to the step before it, not between the two,
              so a wrap can only ever leave an arrow trailing at the end of a
              line, never orphaned at the start of the next one. */}
          <ol className="mt-5 flex flex-wrap items-center gap-y-4">
            {fieldLayer.cycle.map((step, i) => (
              <li key={step} className="flex items-center gap-2.5">
                <Index n={i + 1} />
                <span className="text-[14px] font-medium text-soil">{step}</span>
                {i < fieldLayer.cycle.length - 1 && (
                  <ArrowRight
                    className="w-[18px] h-[18px] text-forest/45 shrink-0 mx-3"
                    aria-hidden="true"
                  />
                )}
              </li>
            ))}
          </ol>
          <p className="mt-5 text-soil/55 text-[14px]">
            Between cycles the radio sleeps. Soil chemistry moves slowly, so waking twice a day is
            enough, and it is what buys the battery its service life.
          </p>
        </div>
        <img
          src={soilProbes}
          alt="Sensor probes seated in a hydroponic growing tray"
          className="rounded-lg w-full h-full min-h-[12rem] object-cover ring-1 ring-soil/10"
        />
      </div>
    </Section>

    {/* ---------- Layer 2 · Transport ---------- */}
    <Section tone="ink" id="transport" className="fieldgrid">
      <Head tone="dark" eyebrow="Layer 02 · Transport" title="Transport & RF link" lead={transportLayer.intro} />

      {/* The column gap is sized to hold the connector that crosses it: the
          arrow starts at left-full and is exactly one gap wide, so it spans
          the whole distance between two hops instead of sitting as a glyph
          nudged off one card's edge.

          Four across is an xl layout, not an lg one. Between 1024 and 1279 the
          container caps at 1024, so four columns plus this gap leave each card
          a 162px content box, and the longest hop title wants 158px unwrapped.
          Under 4px of slack is not a margin, so below xl the hops go two across
          with no arrows and the reading order carries the sequence, exactly as
          they already did on narrow screens. */}
      <div className="mt-12 grid sm:grid-cols-2 xl:grid-cols-4 gap-4 xl:gap-x-10">
        {transportLayer.hops.map((h, i) => (
          <div key={h.name} className="relative rounded-xl bg-white/[0.04] ring-1 ring-white/10 p-6">
            <p className="label text-lime">Hop {String(i + 1).padStart(2, '0')}</p>
            <h3 className="mt-3 font-display font-semibold text-[1.15rem] text-paper">{h.name}</h3>
            <p className="mt-2.5 text-paper/60 text-[14px] leading-relaxed">{h.body}</p>
            {i < transportLayer.hops.length - 1 && (
              <FlowArrow
                dir="right"
                length={HOP_GAP}
                className="hidden xl:block absolute top-1/2 left-full -translate-y-1/2 w-10 h-8 text-lime/70"
              />
            )}
          </div>
        ))}
      </div>

      <div className="mt-8 grid lg:grid-cols-[1.4fr_1fr] gap-6">
        <Card tone="dark" className="p-7">
          <p className="label text-lime flex items-center gap-2">
            <Radio className="w-3.5 h-3.5" /> How the signal travels
          </p>
          <div className="mt-6 space-y-6">
            {transportLayer.travel.map((t) => (
              <div key={t.n} className="flex gap-4">
                <Index n={t.n} tone="dark" />
                <div>
                  <h4 className="font-medium text-paper">{t.title}</h4>
                  <p className="mt-1.5 text-paper/55 text-[14px] leading-relaxed font-mono">{t.body}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card tone="dark" className="p-7">
          <p className="label text-lime">What each transmission carries</p>
          <dl className="mt-5">
            {transportLayer.carries.map((c) => (
              <Spec key={c.field} k={c.field} v={c.body} tone="dark" />
            ))}
          </dl>
          <p className="mt-5 text-paper/50 text-[13.5px] leading-relaxed">{transportLayer.note}</p>
        </Card>
      </div>
    </Section>

    {/* ---------- Layer 3 · Edge compute ---------- */}
    <Section tone="paper" id="edge">
      <Head
        eyebrow="Layer 03 · Edge compute"
        title="The control panel"
        lead={edgeLayer.intro}
      />

      <div className="mt-12 grid lg:grid-cols-3 gap-6">
        {/* Priority is the claim worth leading with here, so it gets the dark
            card rather than being a footnote under a list of parts. */}
        <div className="rounded-xl bg-ink text-paper p-7 flex flex-col">
          <p className="label text-amber">Priority is a safety property</p>
          <p className="mt-5 font-display font-semibold text-[1.35rem] leading-snug text-paper">
            {edgeLayer.priority}
          </p>
          <p className="mt-auto pt-7 text-paper/55 text-[13.5px] leading-relaxed">
            {edgeLayer.headroom}
          </p>
        </div>

        {/* Why */}
        <Card className="p-7">
          <p className="label text-forest">Why it is built this way</p>
          <ul className="mt-5 space-y-4">
            {edgeLayer.why.map((w) => (
              <Bullet key={w}>{w}</Bullet>
            ))}
          </ul>
        </Card>

        {/* What shares the box */}
        <Card className="p-7">
          <p className="label text-forest">Resident concurrently</p>
          <ul className="mt-5">
            {edgeLayer.workloads.map((w) => (
              <li
                key={w}
                className="flex items-center gap-3 py-3 border-b border-soil/10 text-[14.5px] text-soil/75"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-forest shrink-0" />
                {w}
              </li>
            ))}
          </ul>
          <p className="mt-5 text-soil/55 text-[13.5px] leading-relaxed">
            Nothing here has to be started on demand. Every service is already running when a
            reading lands, which is what keeps the loop predictable.
          </p>
        </Card>
      </div>
    </Section>

    {/* ---------- Layer 6 · Explanation ---------- */}
    <Section tone="paper2" id="explanation">
      <Head
        eyebrow="Layer 06 · Explanation (non-actuating)"
        title="The part that answers questions"
        lead="A language model runs on the same box, reads the same database, and can explain any decision the system made. What it cannot do is make one."
      />

      <div className="mt-12 grid lg:grid-cols-[1fr_19rem] gap-8 items-stretch">
        <div className="grid md:grid-cols-3 gap-5">
          {explanationLayer.map((e) => (
            <Card key={e.n} className="p-6 flex flex-col">
              <Index n={e.n} />
              <h3 className="mt-4 font-display font-semibold text-[1.15rem] text-ink">{e.title}</h3>
              <p className="mt-2.5 text-soil/70 text-[14.5px] leading-relaxed">{e.body}</p>
              <p className="mt-auto pt-5 text-[13px] text-forest/80 italic leading-relaxed">{e.note}</p>
            </Card>
          ))}
        </div>
        <img
          src={assistantApp}
          alt="A handset running the plant analysis view of the farm assistant"
          className="rounded-xl w-full h-full min-h-[14rem] object-cover ring-1 ring-soil/10"
        />
      </div>

      <div className="mt-8 rounded-xl border border-dashed border-forest/30 bg-forest/[0.04] p-6 flex items-start gap-4">
        <Bot className="w-5 h-5 text-forest shrink-0 mt-0.5" />
        <p className="text-soil/75 text-[14.5px] leading-relaxed">
          This is the boundary the whole architecture is built around. The assistant reads the
          operational record and the knowledge base to answer questions. It has no route to the
          safety engine and no route to an actuator, so no answer it produces can ever move a pump.
        </p>
      </div>
    </Section>

    {/* ---------- Where layers 4 and 5 live: the end of this page ---------- */}
    <Section tone="soil">
      <Eyebrow tone="dark">Layers 04 and 05</Eyebrow>
      <h2 className="mt-5 font-display font-semibold text-[1.9rem] md:text-[2.4rem] leading-tight text-paper max-w-3xl">
        The two layers that decide and dose have pages of their own
      </h2>
      <p className="mt-5 text-paper/60 text-[15px] leading-relaxed max-w-2xl">
        Everything above measures, moves and stores. What happens between a stored reading and an
        open valve is the part the whole design turns on, so it is set out in full rather than
        summarised here.
      </p>

      <div className="mt-10 grid md:grid-cols-2 gap-6">
        <Link
          to="/farm-tech/dosing"
          className="group rounded-xl bg-white/[0.04] ring-1 ring-white/10 p-7 hover:ring-lime/40 transition-colors"
        >
          <p className="label text-lime">Layer 04 · Decision</p>
          <h3 className="mt-3 font-display font-semibold text-[1.5rem] text-paper">
            How a dose is computed
          </h3>
          <p className="mt-2.5 text-paper/60 text-[14.5px] leading-relaxed">
            The Condition Monitor, the deterministic decision model and the Recipe Engine that turn a
            nutrient requirement into a quantity a pump can execute.
          </p>
          <span className="mt-5 inline-flex items-center gap-2 text-lime text-[14px] font-medium">
            Decision &amp; dosing <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </span>
        </Link>

        <Link
          to="/farm-tech/safety"
          className="group rounded-xl bg-amber/[0.08] ring-1 ring-amber/30 p-7 hover:ring-amber/60 transition-colors"
        >
          <p className="label text-amber">Layer 05 · Safety &amp; actuation</p>
          <h3 className="mt-3 font-display font-semibold text-[1.5rem] text-paper">
            Nothing reaches a valve unauthorised
          </h3>
          <p className="mt-2.5 text-paper/60 text-[14.5px] leading-relaxed">
            The central gate every recommendation must clear, and the mixer unit that closes the
            loop on what was physically dispensed.
          </p>
          <span className="mt-5 inline-flex items-center gap-2 text-amber text-[14px] font-medium">
            Safety &amp; actuation <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </span>
        </Link>
      </div>
    </Section>
  </div>
);

export default Architecture;
